import {
	Accordion,
	Button,
	Card,
	Header,
	Label,
	Message,
	Segment,
} from '@mairistem/composants';
import { getErrorMessage, Icon } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import type { AccordionTitleProps } from 'semantic-ui-react';
import DemandeStatusEnum from '../../enums/demandeStatusEnum';
import type { Role } from '../../interfaces/auth';
import type Collectivite from '../../interfaces/collectivite';
import type { DemandeCompte } from '../../interfaces/demandeCompte';
import type CreatePastellUserResponse from '../../interfaces/utilisateur';
import type {
	UtilisateurCollectivite,
	UtilisateurCollectiviteToCreate,
} from '../../interfaces/utilisateurCollectivite';
import {
	deletePastellUser,
	postPastellUser,
	replacePastellUser,
} from '../../services/pastellService';
import getParapheurUrl from '../../utils/getParapheurUrl';
import UtilisateurBureauModal from './UtilisateurModales/UtilisateurBureauModal';
import UtilisateurCreateModal from './UtilisateurModales/UtilisateurCreateModal/UtilisateurCreateModal';
import UtilisateurDeleteModal from './UtilisateurModales/UtilisateurDeleteModal';
import UtilisateurReplaceModal from './UtilisateurModales/UtilisateurReplaceModal';
import './UtilisateurCollectiviteList.css';

interface UtilisateurCollectiviteListProps {
	demandesComptes: DemandeCompte[];
	handleUpdateDemandeComptes: (updatedDemandeCompte: DemandeCompte) => void;
	collectivites: Collectivite[];
	userRole?: Role;
	initialUtilisateurs: UtilisateurCollectivite[];
}

const today = new Date();

const hasNoRealRole = (
	collectivite: UtilisateurCollectivite['collectivites'][number],
) =>
	!collectivite.roles?.length ||
	(collectivite.roles.length === 1 && collectivite.roles[0] === 'aucun droit');

const hasOnlyAucunDroit = (utilisateur: UtilisateurCollectivite) => {
	// L'utilisateur a des rôles Pastell réels sur au moins une collectivité
	const hasRealPastellRoles = utilisateur.collectivites?.some(
		(c) => !hasNoRealRole(c),
	);
	if (hasRealPastellRoles) return false;

	// L'utilisateur est dans le parapheur (a des bureaux)
	if (
		utilisateur.identifiantParapheur &&
		utilisateur.collectivites?.some((c) => (c.bureaux?.length ?? 0) > 0)
	)
		return false;

	// L'utilisateur est dans slow
	if (utilisateur.collectivites?.some((c) => !!c.collectiviteSlowId))
		return false;

	return true;
};

const UtilisateurCollectiviteList = ({
	demandesComptes,
	handleUpdateDemandeComptes,
	collectivites,
	userRole = 'user',
	initialUtilisateurs,
}: UtilisateurCollectiviteListProps) => {
	const [utilisateurs, setUtilisateurs] =
		React.useState<UtilisateurCollectivite[]>(initialUtilisateurs);
	const [utilisateursOpenIndex, setUtilisateursOpenIndex] = React.useState<
		string[]
	>([]);
	const [collectivitesOpenIndex, setCollectivitesOpenIndex] = React.useState<
		string[]
	>([]);
	const [collectivitesBureauxOpenIndex, setCollectivitesBureauxOpenIndex] =
		React.useState<string[]>([]);
	const [isDeleteConfirmOpened, setIsDeleteConfirmOpened] =
		React.useState(false);
	const [isReplaceConfirmOpened, setIsReplaceConfirmOpened] =
		React.useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
	const [isBureauModalOpen, setIsBureauModalOpen] = React.useState(false);
	const [isDeleteModalLoading, setIsDeleteModalLoading] = React.useState(false);
	const [isModalReplaceLoading, setIsModalReplaceLoading] =
		React.useState(false);
	const [isModalCreateLoading, setIsModalCreateLoading] = React.useState(false);
	const [selectedUtilisateur, setSelectedUtilisateur] =
		React.useState<UtilisateurCollectivite | null>(null);

	const handleUtilisateursClick = (
		_e: React.MouseEvent<HTMLDivElement>,
		data: AccordionTitleProps,
	) => {
		const index = String(data.index);
		const currentIndex = utilisateursOpenIndex.indexOf(index);

		if (currentIndex > -1) {
			setUtilisateursOpenIndex(
				utilisateursOpenIndex.filter((i) => i !== index),
			);
		} else {
			setUtilisateursOpenIndex([...utilisateursOpenIndex, index]);
		}
	};

	const handleCollectivitesClick = (
		_e: React.MouseEvent<HTMLDivElement>,
		data: AccordionTitleProps,
	) => {
		const index = String(data.index);
		const currentIndex = collectivitesOpenIndex.indexOf(index);

		if (currentIndex > -1) {
			setCollectivitesOpenIndex(
				collectivitesOpenIndex.filter((i) => i !== index),
			);
		} else {
			setCollectivitesOpenIndex([...collectivitesOpenIndex, index]);
		}
	};

	const handleCollectivitesBureauxClick = (
		_e: React.MouseEvent<HTMLDivElement>,
		data: AccordionTitleProps,
	) => {
		const index = String(data.index);
		const currentIndex = collectivitesBureauxOpenIndex.indexOf(index);

		if (currentIndex > -1) {
			setCollectivitesBureauxOpenIndex(
				collectivitesBureauxOpenIndex.filter((i) => i !== index),
			);
		} else {
			setCollectivitesBureauxOpenIndex([
				...collectivitesBureauxOpenIndex,
				index,
			]);
		}
	};

	const handleCloseDeleteModal = () => {
		setIsDeleteConfirmOpened(false);
	};

	const handleChangeCreateOpenModal = () => {
		setIsCreateModalOpen(!isCreateModalOpen);
	};

	const handleCloseReplaceModal = () => {
		setIsReplaceConfirmOpened(false);
	};

	const handleCloseBureauModal = () => {
		setIsBureauModalOpen(false);
	};

	const handleUpdateBureaux = () => {
		handleCloseBureauModal();
	};

	const handleOpenDeleteModal = (
		e: React.MouseEvent,
		utilisateur: UtilisateurCollectivite,
	) => {
		e.stopPropagation();
		setIsDeleteConfirmOpened(true);
		setSelectedUtilisateur(utilisateur);
	};

	const handleOpenReplaceModal = (
		e: React.MouseEvent,
		utilisateur: UtilisateurCollectivite,
	) => {
		e.stopPropagation();
		setIsReplaceConfirmOpened(true);
		setSelectedUtilisateur(utilisateur);
	};

	const handleOpenBureauModal = (
		e: React.MouseEvent,
		utilisateur: UtilisateurCollectivite,
	) => {
		e.stopPropagation();
		setIsBureauModalOpen(true);
		setSelectedUtilisateur(utilisateur);
	};

	const handleDeleteUtilisateur = async () => {
		if (!selectedUtilisateur) return;
		try {
			setIsDeleteModalLoading(true);
			await deletePastellUser(selectedUtilisateur);
			setUtilisateurs((old) =>
				old.filter(
					(utilisateur) => utilisateur.login !== selectedUtilisateur.login,
				),
			);
			handleCloseDeleteModal();

			toast.success('Utilisateur supprimé avec succès.');
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					"Une erreur est survenue lors de la suppression de l'utilisateur.",
				),
			);
		} finally {
			setIsDeleteModalLoading(false);
		}
	};

	const getLogin = (utilisateur: UtilisateurCollectivite): string => {
		if (utilisateur?.identifiantUtilisateurSlow) {
			return utilisateur?.collectivites[0]?.loginSlow ?? utilisateur?.login;
		}

		return utilisateur?.login;
	};

	const handleReplaceUser = async (demandeDeCompteId: number) => {
		if (!selectedUtilisateur) return;
		try {
			setIsModalReplaceLoading(true);
			await replacePastellUser(demandeDeCompteId, selectedUtilisateur);

			setUtilisateurs((old) =>
				old.map((utilisateur) => {
					if (utilisateur.login === selectedUtilisateur.login) {
						return { ...utilisateur, isUpdated: true };
					}
					return utilisateur;
				}),
			);

			const demandeCompte = demandesComptes.find(
				(d) => d.identifiant === demandeDeCompteId,
			);
			if (demandeCompte) {
				handleUpdateDemandeComptes({
					...demandeCompte,
					statusCode: DemandeStatusEnum.EN_COURS,
				});
			}

			toast.success('Votre demande de remplacement a été envoyée avec succès.');

			handleCloseReplaceModal();
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					"Une erreur est survenue lors du remplacement de l'utilisateur.",
				),
			);
		} finally {
			setIsModalReplaceLoading(false);
		}
	};

	const handleCreatePastellUser = async (
		utilisateur: UtilisateurCollectiviteToCreate,
		certificat?: File,
	) => {
		// Filtrer les collectivités pour ne garder que celles qui ont des bureaux ou un collectiviteSlowId
		const collectivitesFiltered = utilisateur.collectivites?.filter(
			(collectivite) => {
				const hasBureaux =
					collectivite.bureaux && collectivite.bureaux.length > 0;
				// Vérifier si la propriété collectiviteSlowId existe (même si null)
				const hasSlowIdProperty = Object.hasOwn(
					collectivite,
					'collectiviteSlowId',
				);
				return hasBureaux || hasSlowIdProperty;
			},
		);

		const utilisateurToSend = {
			...utilisateur,
			collectivites: collectivitesFiltered,
		};

		setIsModalCreateLoading(true);

		try {
			const response: CreatePastellUserResponse = await postPastellUser(
				utilisateurToSend,
				certificat,
			);

			// Créer le nouvel utilisateur à partir de la réponse
			const newUtilisateur: UtilisateurCollectivite = {
				login: response.pastell.login,
				email: response.pastell.email,
				nom: response.pastell.nom,
				prenom: response.pastell.prenom,
				active: true,
				identifiantPastell: response.pastell.id,
				multiNomPrenom: false,
				collectivites:
					collectivitesFiltered?.map((collectivite) => ({
						id_e: collectivite.id_e,
						siren: collectivite.siren,
						libelle: collectivite.libelle,
						codeClientSF: collectivite.codeClientSF,
						roles: collectivite.roles || [],
						loginSlow: collectivite.loginSlow || '',
						roleSlow: collectivite.roleSlow || '',
						collectiviteSlowId: collectivite.collectiviteSlowId
							? Number(collectivite.collectiviteSlowId)
							: 0,
						collectiviteSlowName: collectivite.collectiviteSlowName || '',
						bureaux: collectivite.bureaux?.map((b) => ({
							id: Number(b.id),
							name: b.name,
							parapheur: b.parapheur,
							tenantId: b.tenantId,
						})),
						...(collectivite.identifiantUtilisateurSlow
							? {
									identifiantUtilisateurSlow: Number(
										collectivite.identifiantUtilisateurSlow,
									),
								}
							: {}),
					})) || [],
				isUpdated: false,
			};

			// Ajouter le nouvel utilisateur à la liste
			setUtilisateurs((old) => [newUtilisateur, ...old]);

			toast.success('Utilisateur créé avec succès.');
			handleChangeCreateOpenModal();
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					"Une erreur est survenue lors de la création de l'utilisateur.",
				),
			);
		} finally {
			setIsModalCreateLoading(false);
		}
	};

	return (
		<>
			<Segment className="utilisateur-list">
				<Header textAlign="left" className="infos-list-header">
					<p>Utilisateurs et bureaux de la collectivité</p>
					{userRole === 'admin' && (
						<Button onClick={handleChangeCreateOpenModal} primary>
							Ajouter un utilisateur
						</Button>
					)}
				</Header>

				<div className="list-content">
					{utilisateurs.length === 0 ? (
						<Message info>Aucun utilisateur récupéré</Message>
					) : (
						utilisateurs
							?.filter(
								(utilisateur) =>
									userRole === 'admin' || !hasOnlyAucunDroit(utilisateur),
							)
							.map((utilisateur) => (
								<Card fluid key={utilisateur.login}>
									<Card.Content>
										<Accordion key={utilisateur.login}>
											<Accordion.Title
												active={utilisateursOpenIndex.includes(
													getLogin(utilisateur),
												)}
												index={getLogin(utilisateur)}
												onClick={handleUtilisateursClick}
											>
												<div className="info-container">
													<div className="info-icons">
														<Icon name="dropdown" />
														<span className="info-icon">
															<Icon
																iconSet="Lucide"
																size={20}
																name="User"
																color="var(--accent)"
															/>
														</span>
													</div>
													<div
														className="bloc-info"
														data-testid={`utilisateur-info-${utilisateur.login}`}
													>
														{utilisateur.prenom} {utilisateur.nom}
														<div className="utilisateur-email">
															{' '}
															{utilisateur.login ?? utilisateur.email}
														</div>
													</div>

													<div className="info-actions">
														{utilisateur.isUpdated && (
															<Label>
																<Icon name="time" />
																Traitement en attente
															</Label>
														)}
														{userRole !== 'adminConsult' && (
															<Button
																primary
																onClick={(e) =>
																	handleOpenReplaceModal(e, utilisateur)
																}
																title="Remplacer l'utilisateur"
																disabled={utilisateur.isUpdated}
															>
																Remplacer l&apos;utilisateur
															</Button>
														)}
														{userRole === 'admin' && (
															<>
																<Button
																	icon
																	compact
																	basic
																	onClick={(e) =>
																		handleOpenBureauModal(e, utilisateur)
																	}
																	title="Ajouter un bureau"
																>
																	<Icon
																		name="Plus"
																		iconSet="Lucide"
																		size={20}
																	/>
																</Button>
																<Button
																	icon
																	compact
																	basic
																	onClick={(e) =>
																		handleOpenDeleteModal(e, utilisateur)
																	}
																	title="Supprimer l'utilisateur"
																>
																	<Icon
																		name="Trash2"
																		iconSet="Lucide"
																		size={20}
																		color="red"
																	/>
																</Button>
															</>
														)}
													</div>
												</div>
											</Accordion.Title>
											<Accordion.Content
												active={utilisateursOpenIndex.includes(
													getLogin(utilisateur),
												)}
												className="utilisateur-content"
											>
												{utilisateur.collectivites?.map((collectivite) => (
													<Accordion key={collectivite?.libelle}>
														<Accordion.Title
															active={collectivitesOpenIndex.includes(
																getLogin(utilisateur) +
																	collectivite.id_e.toString(),
															)}
															index={
																getLogin(utilisateur) +
																collectivite.id_e.toString()
															}
															onClick={handleCollectivitesClick}
														>
															<div className="info-container">
																<div className="info-icons">
																	<Icon name="dropdown" />
																	<span className="info-icon">
																		<Icon
																			iconSet="Lucide"
																			name="Building"
																			color="var(--primary)"
																			size={20}
																		/>
																	</span>
																</div>
																<div className="bloc-info">
																	{collectivite?.libelle}
																</div>
															</div>
														</Accordion.Title>
														<Accordion.Content
															active={collectivitesOpenIndex.includes(
																getLogin(utilisateur) +
																	collectivite.id_e.toString(),
															)}
															className="collectivite-content"
														>
															{/* Utilisateur pastell  */}
															{!!utilisateur.identifiantPastell &&
																!hasNoRealRole(collectivite) && (
																	<div className="user-role">
																		<Icon
																			iconSet="Lucide"
																			name="Circle"
																			size={20}
																		/>
																		Agent acte
																	</div>
																)}

															{userRole === 'admin' &&
																Array.isArray(collectivite.roles) &&
																hasNoRealRole(collectivite) && (
																	<div className="user-role">
																		<Icon
																			iconSet="Lucide"
																			name="Circle"
																			size={20}
																		/>
																		Aucun rôle pastell
																	</div>
																)}

															{!!collectivite?.collectiviteSlowId && (
																<div className="user-role">
																	<Icon
																		iconSet="Lucide"
																		name="Circle"
																		size={20}
																	/>
																	Agent télétransmetteur
																	{collectivite?.certificat?.dateFinValidite &&
																	collectivite.certificat.dateFinValidite >
																		today ? (
																		<span className="certificat-valide">
																			Valide jusqu&apos;au{' '}
																			{collectivite.certificat.dateFinValidite.toLocaleDateString(
																				'fr-FR',
																			)}
																		</span>
																	) : (
																		<span className="certificat-invalide">
																			<Icon
																				iconSet="Lucide"
																				name="TriangleAlert"
																				size={14}
																				color="#DC2626"
																			/>
																			Expiré le{' '}
																			{collectivite?.certificat?.dateFinValidite?.toLocaleDateString(
																				'fr-FR',
																			)}
																		</span>
																	)}
																</div>
															)}
															{/* Présent sur le parapheur  */}
															{!!utilisateur.identifiantParapheur &&
																(collectivite?.bureaux?.length ?? 0) > 0 && (
																	<Accordion
																		key={collectivite?.libelle}
																		className="signataire-accordion"
																	>
																		<Accordion.Title
																			onClick={handleCollectivitesBureauxClick}
																			active={collectivitesBureauxOpenIndex.includes(
																				getLogin(utilisateur) +
																					collectivite.id_e.toString(),
																			)}
																			index={
																				getLogin(utilisateur) +
																				collectivite.id_e.toString()
																			}
																		>
																			<div className="user-role">
																				<Icon name="dropdown" />
																				Signataire
																			</div>
																		</Accordion.Title>
																		<Accordion.Content
																			active={collectivitesBureauxOpenIndex.includes(
																				getLogin(utilisateur) +
																					collectivite.id_e.toString(),
																			)}
																			className="collectivite-content"
																		>
																			{(collectivite?.bureaux?.length ?? 0) >
																				0 &&
																				collectivite?.bureaux?.map((bureau) => (
																					<div
																						className="user-role"
																						key={bureau.id}
																					>
																						<Icon
																							iconSet="Lucide"
																							name="File"
																							size={20}
																						/>
																						{bureau?.name}
																						{bureau?.parapheur && (
																							<>
																								(
																								<a
																									href={getParapheurUrl(
																										bureau.parapheur,
																									)}
																									target="_blank"
																									rel="noopener noreferrer"
																								>
																									{getParapheurUrl(
																										bureau.parapheur,
																									)}
																								</a>
																								)
																							</>
																						)}
																					</div>
																				))}
																		</Accordion.Content>
																	</Accordion>
																)}
														</Accordion.Content>
													</Accordion>
												))}
											</Accordion.Content>
										</Accordion>
									</Card.Content>
								</Card>
							))
					)}
				</div>
			</Segment>

			{isDeleteConfirmOpened && selectedUtilisateur && (
				<UtilisateurDeleteModal
					selectedUtilisateur={selectedUtilisateur}
					onClose={handleCloseDeleteModal}
					onDelete={handleDeleteUtilisateur}
					isLoading={isDeleteModalLoading}
				/>
			)}

			{isReplaceConfirmOpened && selectedUtilisateur && (
				<UtilisateurReplaceModal
					selectedUtilisateur={selectedUtilisateur}
					demandesComptes={demandesComptes}
					onClose={handleCloseReplaceModal}
					onValidate={handleReplaceUser}
					isLoading={isModalReplaceLoading}
					onUpdateDemandeCompte={handleUpdateDemandeComptes}
				/>
			)}

			{isCreateModalOpen && (
				<UtilisateurCreateModal
					onClose={handleChangeCreateOpenModal}
					isLoading={isModalCreateLoading}
					onCreate={handleCreatePastellUser}
					entitesPastell={collectivites}
				/>
			)}

			{isBureauModalOpen && selectedUtilisateur && (
				<UtilisateurBureauModal
					selectedUtilisateur={selectedUtilisateur}
					onClose={handleCloseBureauModal}
					onUpdateBureaux={handleUpdateBureaux}
				/>
			)}
		</>
	);
};

export default UtilisateurCollectiviteList;
